import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";
import StatCards from "./StatCards";
import DashboardPendingApproval from "./DashboardPendingApproval";
import DashboardSupportTickets from "./DashboardSupportTickets";
import Loader from "../../../components/admin/Loader";
import { getDashboardData, getSubtreeCourseSummary, listGenericSurveys, getGenericSurveyAnalytics } from "../../../services/adminService";
import { getProfile } from "../../../services/userService";
import { usePermissions } from "../../../context/PermissionsContext";

function Dashboard() {
  const navigate = useNavigate();
  const { permissions } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [allDashboardData, setAllDashboardData] = useState(null);
  const [error, setError] = useState(null);

  const [profileData, setProfileData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [teamAllRows, setTeamAllRows] = useState([]);
  const [teamAttentionRows, setTeamAttentionRows] = useState([]);


  useEffect(() => {
    const hasAnyDashboardPermission =
      permissions.dashboard_management_cards_view ||
      permissions.dashboard_management_graphs_view ||
      permissions.dashboard_management_view_latest_updates_view;

    if (permissions && Object.keys(permissions).length > 0 && !hasAnyDashboardPermission) {
      navigate('/admin/courses', { replace: true });
    }
  }, [permissions, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [response, surveyResp] = await Promise.all([
        getDashboardData(),
        listGenericSurveys({ page: 1, limit: 1000 })
      ]);
      const payload = response && response.success === true ? response.data : response;
      if (payload && typeof payload === 'object') {
        const surveys = surveyResp?.success ? (surveyResp.data?.surveys || []) : [];
        
        // Fetch analytics for each survey to get real total_responses
        // We use limit: 1 since we only need the total_responses count from the response object
        const analyticsPromises = surveys.map(s => getGenericSurveyAnalytics(s.id, { limit: 1 }));
        const analyticsResps = await Promise.all(analyticsPromises);
        
        const totalSurveyResponses = analyticsResps.reduce((acc, res) => {
          return acc + Number(res?.data?.total_responses || 0);
        }, 0);
        
        setAllDashboardData({
          ...payload,
          totalSurveyResponses,
          totalSurveys: surveys.length
        });
      } else {
        setError("Failed to fetch dashboard data");
      }
    } catch (error) {
      console.error('Error in fetchDashboardData:', error);
      setError("An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const prof = await getProfile();
        const profile = prof?.data;
        setProfileData(profile);

        if (!profile?.has_reportees) {
          setTeamAllRows([]);
          setTeamAttentionRows([]);
          return;
        }

        setTeamLoading(true);
        const res = await getSubtreeCourseSummary();
        const rows = Array.isArray(res?.data?.rows)
          ? res.data.rows
          : Array.isArray(res?.data)
            ? res.data
            : [];
        const normalized = Array.isArray(rows) ? rows : [];
        setTeamAllRows(normalized);

        const attention = normalized
          .slice()
          .filter((r) => Number(r?.enrolled_count || 0) > 0)
          .sort((a, b) => Number(a?.completion_percent ?? 101) - Number(b?.completion_percent ?? 101))
          .slice(0, 5);
        setTeamAttentionRows(attention);
      } catch (e) {
        setTeamAllRows([]);
        setTeamAttentionRows([]);
      } finally {
        setTeamLoading(false);
      }
    };

    fetchTeam();
  }, []);



  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[360px] rounded-2xl bg-gray-50/50 border border-gray-100">
        <Loader />
      </div>
    );
  }

  const hasAnyDashboardPermission =
    permissions.dashboard_management_cards_view ||
    permissions.dashboard_management_graphs_view ||
    permissions.dashboard_management_view_latest_updates_view;

  if (!hasAnyDashboardPermission) {
    return null;
  }

  if (error) {
    return (
      <div className="flex justify-center items-center min-h-[360px] rounded-2xl border border-red-100 bg-red-50/50">
        <div className="text-center px-6">
          <Icon icon="lucide:alert-circle" width="48" height="48" className="text-red-500 mb-3 mx-auto" />
          <h5 className="text-red-700 font-medium mb-2">{error}</h5>
          <button
            type="button"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-red-100 text-red-700 font-medium hover:bg-red-200 transition-colors"
            onClick={fetchDashboardData}
          >
            <Icon icon="lucide:refresh-cw" width="18" height="18" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Stat Cards — full width top row */}
      {permissions.dashboard_management_cards_view && (
        <div className="mb-5">
          <StatCards allDashboardData={allDashboardData} />
        </div>
      )}

      {Boolean(profileData?.has_reportees) && (
        <div className="mb-5 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">Team Course Progress</h2>
              <p className="text-xs text-gray-400 mt-0.5">Quick view of your reportees (click to open Reporting)</p>
            </div>
            <button
              onClick={() => navigate('/admin/roles-and-access?tab=reporting')}
              className="text-xs font-medium text-[#7083FF] hover:text-[#5a6be8] flex items-center gap-1 transition-colors"
            >
              View All <Icon icon="lucide:arrow-right" width="13" />
            </button>
          </div>

          {teamLoading ? (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          ) : teamAllRows.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-6">
              <div className="w-12 h-12 rounded-2xl bg-gray-50 flex items-center justify-center mb-3">
                <Icon icon="lucide:users" width="22" className="text-gray-300" />
              </div>
              <p className="text-sm font-medium text-gray-400">No team progress data</p>
              <p className="text-xs text-gray-300 mt-0.5">Assign courses to your reportees to see progress.</p>
            </div>
          ) : (
            (() => {
              const totals = (teamAllRows || []).reduce(
                (acc, r) => {
                  acc.enrolled += Number(r?.enrolled_count || 0);
                  acc.completed += Number(r?.completed_count || 0);
                  acc.inProgress += Number(r?.in_progress_count || 0);
                  acc.notStarted += Number(r?.not_started_count || 0);
                  return acc;
                },
                { enrolled: 0, completed: 0, inProgress: 0, notStarted: 0 }
              );
              const denom = totals.completed + totals.inProgress + totals.notStarted;
              const completedPct = denom ? (totals.completed / denom) * 100 : 0;
              const inProgressPct = denom ? (totals.inProgress / denom) * 100 : 0;
              const notStartedPct = denom ? (totals.notStarted / denom) * 100 : 0;

              return (
                <div className="p-5 space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-gray-700">Team Breakdown</div>
                      <div className="text-[11px] text-gray-400">{totals.enrolled} enrolled</div>
                    </div>
                    <div className="w-full h-3 rounded-full bg-gray-100 overflow-hidden flex">
                      <div className="h-full bg-emerald-500" style={{ width: `${completedPct}%` }} />
                      <div className="h-full bg-amber-400" style={{ width: `${inProgressPct}%` }} />
                      <div className="h-full bg-slate-300" style={{ width: `${notStartedPct}%` }} />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-3 text-[11px]">
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="w-2 h-2 rounded-full bg-emerald-500" />
                        <span className="font-semibold">Completed</span>
                        <span className="text-gray-400">{totals.completed}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="w-2 h-2 rounded-full bg-amber-400" />
                        <span className="font-semibold">In Progress</span>
                        <span className="text-gray-400">{totals.inProgress}</span>
                      </div>
                      <div className="flex items-center gap-2 text-gray-600">
                        <span className="w-2 h-2 rounded-full bg-slate-300" />
                        <span className="font-semibold">Not Started</span>
                        <span className="text-gray-400">{totals.notStarted}</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-xs font-semibold text-gray-700">Needs Attention</div>
                      <div className="text-[11px] text-gray-400">Lowest completion</div>
                    </div>
                    <div className="divide-y divide-gray-100 rounded-2xl border border-gray-100 overflow-hidden">
                      {(teamAttentionRows || []).map((r, idx) => {
                        const completion = r?.completion_percent == null ? null : Number(r.completion_percent);
                        return (
                          <div
                            key={r?.user_id ?? idx}
                            className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 cursor-pointer"
                            onClick={() => navigate('/admin/roles-and-access?tab=reporting')}
                            role="presentation"
                          >
                            <div className="min-w-0">
                              <div className="text-xs font-semibold text-gray-900 truncate max-w-[260px]">{r?.user_name || 'User'}</div>
                              <div className="text-[11px] text-gray-400 truncate max-w-[260px]">{r?.user_email || ''}</div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="text-[11px] text-gray-500">{Number(r?.completed_count || 0)}/{Number(r?.enrolled_count || 0)}</div>
                              <div className={`text-xs font-bold ${completion != null && completion < 40 ? 'text-rose-600' : 'text-gray-700'}`}>
                                {completion == null ? '—' : `${completion}%`}
                              </div>
                              <Icon icon="lucide:chevron-right" width="14" className="text-gray-300" />
                            </div>
                          </div>
                        );
                      })}
                      {(!teamAttentionRows || teamAttentionRows.length === 0) && (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">No reportees with enrolled courses</div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()
          )}
        </div>
      )}

      {/* Pending Approval Table */}
      {permissions.dashboard_management_view_latest_updates_view && (
        <div className="mb-5">
          <DashboardPendingApproval />
        </div>
      )}

      {/* Support Tickets Table */}
      {permissions.dashboard_management_view_latest_updates_view && (
        <div className="mb-5">
          <DashboardSupportTickets />
        </div>
      )}

      {/* No permissions */}
      {!permissions.dashboard_management_cards_view &&
        !permissions.dashboard_management_graphs_view &&
        !permissions.dashboard_management_view_latest_updates_view && (
          <div className="flex justify-center items-center min-h-[360px] rounded-2xl border border-amber-100 bg-amber-50/50">
            <div className="text-center px-6">
              <Icon icon="lucide:shield-x" width="48" height="48" className="text-amber-500 mb-3 mx-auto" />
              <h5 className="text-amber-800 font-medium mb-2">Access Restricted</h5>
              <p className="text-amber-700/80 text-sm">You don't have permission to view dashboard content.</p>
            </div>
          </div>
        )}
    </>
  );
}

export default Dashboard;
